import { OidcClient, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

import CommonAuthService from '../lib/services/CommonAuth.service';
import { IAuthService } from '../lib/services/types';

import config from '../config';
import { UI_ROUTES } from '../util/routes';

interface AuthResult {
  rawState: any,
}

export default class AuthService extends CommonAuthService implements IAuthService {
  userManager: InstanceType<typeof UserManager>;

  oidcClient: InstanceType<typeof OidcClient>;

  constructor() {
    super();

    const { issuer, clientId, scopes, loginRedirectUrl, postLogoutRedirectUrl } = config.auth;

    const settings: UserManagerSettings = {
      authority: issuer,
      client_id: clientId,
      response_type: 'code',
      response_mode: 'query',
      redirect_uri: loginRedirectUrl,
      scope: scopes.join(' '),
      automaticSilentRenew: false,
      userStore: new WebStorageStateStore({ store: localStorage }),
      silentRequestTimeoutInSeconds: 5,
      post_logout_redirect_uri: postLogoutRedirectUrl,
    };

    this.oidcClient = new OidcClient(settings);
    this.userManager = new UserManager(settings);
  }

  getUserManager = () => this.userManager;

  private createPromiseResult(method: string, ...params: any[]) {
    return new Promise((success: any, fail: any) => {
      withCallbacks(this.getUserManager(), method,
        (result: AuthResult) => {
          if (result != null) {
            result.rawState = { ...result };
            success(result);
          } else {
            fail(new Error('Failed to get result!'));
          }
        },
        (error: any) => {
          fail(error);
        },
        ...params);
    });
  }

  async authorize(key: string) {
    switch (key) {
      case 'login': {
        const searchParams = new URLSearchParams({
          returnUrl: `${window.location.origin}${UI_ROUTES.overview}`,
        });

        window.location.href = `${config.auth.bffLoginUrl}?${searchParams.toString()}`;
        return new Promise(() => {});
      }
      case 'callback':
        return this.createPromiseResult('signinCallback');
      default:
        throw new Error(`AuthService: Invalid key: ${key}`);
    }
  }

  signout = () => this.getUserManager().removeUser();
}

function withCallbacks(userManager: any, name: string, callback: any, errorCallback: any, ...params: any) {
  return userManager[name](...params)
    .then((result: any) => callback(result))
    .catch((error: any) => errorCallback(error));
}
